import AdyenCheckout from '@adyen/adyen-web';
import SecuredFieldsElement from '@adyen/adyen-web/dist/types/components/SecuredFields/SecuredFields';
import { TFunction } from 'i18next';
import { useEffect, useRef } from 'react';

import {
  getAdyenCardBrands,
  overrideAdyenTranslations,
  styles as adyenFormStyles,
} from '@components/AdyenCreditCardForm/AdyenCreditCardForm.static';
import { normalizeLocaleForAdyen } from '@config/Adyen.static';
import { CreditCardBrand, Locale } from '@types-internal';
import { AdyenConfig, AdyenCardBrand } from '@types-internal/AdyenTypes';

export interface AdyenSecureFieldsConfig extends AdyenConfig {
  locale: Locale;
  t: TFunction;
  containerId: string;
  onChange: (state: unknown) => void;
  onFieldValid: (data: unknown) => void;
  onBrand: (brandData: unknown) => void;
  onBinLookup?: (binLookupResult: BinLookupResult) => void;
  onFatalError: (error: unknown, hideForm?: boolean) => void;
  onFocus: (data: unknown) => void;
  isModernize?: boolean;
}

export interface BinLookupResult {
  detectedBrands: AdyenCardBrand[] | null;
  supportedBrands: AdyenCardBrand[] | null;
}

/**
 * Effect hook over the initialization of the 'securedfields' of AdyenCheckout.
 * The inputs 'encryptedCardNumber', 'encryptedExpiryMonth', 'encryptedExpiryYear' and 'encryptedSecurityCode' will be managed by Adyen once this hook finishes.
 */
const useAdyenSecureFields = (
  config: AdyenSecureFieldsConfig,
  supportedCreditCardBrands: CreditCardBrand[],
): SecuredFieldsElement | undefined => {
  const securedFields = useRef<SecuredFieldsElement>();
  const {
    adyenEnvironment,
    locale,
    t,
    containerId,
    clientKey,
    onChange,
    onFocus,
    onFieldValid,
    onBrand,
    onBinLookup,
    onFatalError,
    isModernize,
  } = config;

  useEffect(() => {
    async function initialize() {
      try {
        const adyenLocale = normalizeLocaleForAdyen(locale);
        const adyen = await AdyenCheckout({
          environment: adyenEnvironment,
          translations: { [adyenLocale]: overrideAdyenTranslations(t, isModernize) },
          locale: adyenLocale,
          clientKey,
          onChange,
        });

        securedFields.current = adyen.create('securedfields', {
          type: 'card',
          brands: getAdyenCardBrands(supportedCreditCardBrands),
          styles: adyenFormStyles,
          onBrand,
          onFieldValid,
          onFocus,
          onBinLookup,
        });

        securedFields.current.mount(`#${containerId}`);
      } catch (error) {
        onFatalError(error, false);
      }
    }

    initialize();
    /**
     * This dependency list needs to be revisted/retested, calling the initialize function more then once will
     * cause Adyen to throw an error
     * We need to investigate:
     *  * In case we need to update the adyen config we need to do something else and not initialize again because it will throw an error
     *  * In case we don't ever need to update the adyen config we should remove this part as it is missleading
     */
  }, [
    supportedCreditCardBrands,
    adyenEnvironment,
    locale,
    clientKey,
    onBrand,
    onFieldValid,
    onChange,
    onFatalError,
  ]);

  return securedFields.current;
};

export default useAdyenSecureFields;
